import {MaterialStorage} from '@domain/material-storage'
import {useMaterials} from '@domain/materials'
import {completionScoreBandColor} from '@hconnect/common/utils'
import {DataTable} from '@hconnect/uikit'
import {Column} from '@hconnect/uikit/src/lib2/components/table/DataTable.types'
import {Box, Chip, useTheme} from '@mui/material'
import {get} from 'lodash'
import React, {FC, useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {SILO_FILL_LEVELS_WIDGET_NAMESPACE} from '../consts'

const OPACITY_LEVEL = 10

type SilosProps = {
  silos: MaterialStorage[]
  loading?: boolean
  plantCode: string
}

export const SilosDataTable: FC<SilosProps> = ({silos, loading, plantCode}) => {
  const {
    data: materialsData,
    isLoading: isMaterialsLoading,
    isError: isMaterialsError
  } = useMaterials({plantCode})
  const materialsById = useMemo(
    () => new Map<number, string>((materialsData ?? []).map((m) => [m.id, m.name])),
    [materialsData]
  )
  const {t} = useTranslation(SILO_FILL_LEVELS_WIDGET_NAMESPACE)
  const theme = useTheme()

  const columns: Column<MaterialStorage>[] = useMemo(
    () => [
      {
        key: 'name',
        label: t('siloName')
      },
      {
        key: 'materialId',
        label: t('storedMaterial'),
        customTemplate: (silo) =>
          silo.materialId === null ? '' : (materialsById.get(silo.materialId) ?? '')
      },
      {
        key: 'fillLevel',
        label: t('fillLevel'),
        customTemplate: (silo) => {
          const level = silo?.currentStorageLevel?.level
          return Number.isFinite(level) ? Math.round(level) : ''
        }
      },
      {
        key: 'maxCapacity',
        label: t('maxCapacity'),
        customTemplate: (silo) => silo.capacity
      },
      {
        key: 'fillLevelPercentage',
        label: t('fillLevelPercentage'),
        customTemplate: (silo) => {
          const fillLevelPercentage =
            silo.capacity === 0 ? 0 : (silo.currentStorageLevel.level / silo.capacity) * 100
          return (
            <Chip
              label={`${fillLevelPercentage.toFixed()}%`}
              sx={{
                backgroundColor: `${get(theme.palette, completionScoreBandColor(fillLevelPercentage))}${OPACITY_LEVEL}`,
                color: completionScoreBandColor(fillLevelPercentage),
                borderColor: completionScoreBandColor(fillLevelPercentage),
                borderWidth: '1px',
                borderStyle: 'solid'
              }}
            />
          )
        }
      }
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [materialsById, t, theme]
  )

  return (
    <Box sx={{overflowX: 'auto', width: '100%'}}>
      <DataTable
        data-test-id="silo-fills-table"
        columns={columns}
        data={silos}
        loading={loading || isMaterialsLoading}
        keyExtractor={(silo) => silo.id.toString()}
        emptyMessage={isMaterialsError ? t('fetchingError') : t('noData')}
      />
    </Box>
  )
}
